import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** POST body for backend `pharmacy/appeal/send`. */
export interface SendAppealPayload {
  identifier: string;
  appeal_type?: string;
  appeal_reason?: string | null;
}

/** Parsed response from backend `pharmacy/appeal/send`. */
export interface SendAppealResponse {
  message?: string;
  success?: boolean;
  identifier?: string;
}

/**
 * Commits a generated appeal case to the Appeal tab.
 *
 * @param payload - Order identifier and optional appeal metadata
 * @returns Parsed JSON response from backend service
 * @throws {Error} When auth is missing or the API returns a non-OK status
 */
export const sendAppeal = async (
  payload: SendAppealPayload,
): Promise<SendAppealResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.SEND_APPEAL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({
        identifier: payload.identifier,
        appeal_type: payload.appeal_type ?? "Fax",
        appeal_reason: payload.appeal_reason ?? undefined,
      }),
    });

    const responseText = await response.text();
    let parsed: SendAppealResponse = {};

    if (responseText.trim()) {
      try {
        parsed = JSON.parse(responseText) as SendAppealResponse;
      } catch {
        parsed = { message: responseText };
      }
    }

    if (!response.ok) {
      throw new Error(
        parsed.message ||
          `Failed to send appeal: ${response.status} - ${responseText}`,
      );
    }

    return parsed;
  } catch (error) {
    logError(error as Error, "sendAppeal");
    throw error;
  }
};
